/* eslint-disable @typescript-eslint/no-explicit-any */
// DcData.tsx
import React from "react";
// Import the SVG icons as React components (optional, or use your own icons)
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { ResourceTagsTableCell } from "../../../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../../../Shared/mapKeListResourceTags";

export interface DataCenter {
    id: number | string;
    name: string;
    tagsRich?: ResourceTagSummary[];
    isDraft: boolean;
    subscriptionId: string;
    terms: number | string;
    mbps: number | string;
    tenantNameDisplay: string;
    automaticVlan: boolean;
    vlan: string;
    // Detail response fields
    kontrolEdgePortId?: number;
    redundancy?: boolean;
    countryCode?: string;
    autoRenew?: boolean;
    p2pAddressing?: {
      mask?: string;
      peerIp?: string;
      gateway?: string;
      network?: string;
    };
    bgpConfig?: any;
    kontrolEdgeLagId?: number | null;
    latencyTargets?: any;
    kontrolEdgePortIdA?: number;
    kontrolEdgePortIdB?: number;
    createdAt?: string;
    updatedAt?: string;
    odooMetadata?: any;
    kontrolEdgePort?: {
      id?: number;
      subid?: string;
      name?: string;
      tenantId?: number;
      datacenterId?: string;
      hardwareId?: string;
      capacityMbps?: number;
      tenant?: {
        id?: number;
        Name?: string;
        nameDisplay?: string;
        chargebeeCustomerId?: string;
      };
      datacenter?: {
        datacenterId?: string;
        name?: string;
        displayName?: string;
        city?: string;
      };
      hardware?: {
        id?: number;
        hostname?: string;
        model?: string;
      };
    };
    kontrolEdgePortA?: {
      id?: number;
      subid?: string;
      name?: string;
      tenantId?: number;
      datacenterId?: string;
      datacenter?: {
        datacenterId?: string;
        name?: string;
        displayName?: string;
      };
    };
    kontrolEdgePortB?: {
      id?: number;
      subid?: string;
      name?: string;
      tenantId?: number;
      datacenterId?: string;
      datacenter?: {
        datacenterId?: string;
        name?: string;
        displayName?: string;
      };
    };
    // Polymorphic B endpoint. When the B side is not a port it is an SDWAN hub
    // or a FWAAS; endpointBType is the discriminator and only the matching
    // endpointB* object is populated. Legacy port-to-port DCIs leave these
    // undefined and keep using kontrolEdgePortB.
    endpointBType?: "port" | "sdwan_hub" | "fwaas";
    endpointBId?: number;
    endpointBSdwanHub?: {
      id?: number;
      name?: string;
      subscriptionId?: string | null;
      tenantId?: number;
    } | null;
    endpointBFwaas?: {
      id?: number;
      name?: string;
      subscriptionId?: string | null;
      tenantId?: number;
    } | null;
    // legacy fields used by existing forms/view modals (optional)
    datacenterId?: string;
    direction?: string;
    gps?: string;
    capacityKW?: number;
    phase?: string;
    cage?: string;
    rackId?: string;
    rackCapacity?: number;
    patchPanelODF?: string;
    rackSecurityPin?: string;
    status?: string;
    visible?: boolean;
    providerId?: string;
    providerCode?: string;
    city?: string;
    displayName?: string;
  }

/**
 * Define table columns using our interface.
 * Only some fields (Name, Direction, Capacity, Status) are displayed in the table.
 */
export const columns: GenericTableColumn<DataCenter>[] = [
  {
    header: "Id",
    accessor: "subscriptionId",
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Tag",
    renderCell: (row) => (
      <ResourceTagsTableCell
        tagsRich={row.tagsRich}
        maxVisible={1}
      />
    ),
  },
  {
    header: "Status",
    renderCell: (row) => (
      <GenericTag
        variant={row.isDraft ? "warning" : "success"}
        label={row.isDraft ? "Draft" : "Active"}
        size="small"
      />
    ),
  },
  {
    header: "Terms",
    accessor: "terms",
  },
  {
    header: "Mbps",
    accessor: "mbps",
  },
  {
    header: "Tenant",
    accessor: "tenantNameDisplay",
  },
  {
    header: "Automatic VLAN",
    renderCell: (row) => (row.automaticVlan ? "Yes" : "No"),
  },
  {
    header: "VLAN",
    accessor: "vlan",
  },
];


export const shapeDataCenterData = (rows: any[]): DataCenter[] =>
  rows.map((r) => {
    const tagsRich = mapKeListResourceTags(r);
    return {
    id: r.id ?? "",
    name: r.name ?? r.subscriptionId ?? "",
    tagsRich,
    isDraft: Boolean(r.isDraft),
    subscriptionId: r.subscriptionId ?? "",
    terms: r.terms ?? "",
    mbps: r.mbps ?? "",
    tenantNameDisplay: r.kontrolEdgePort?.tenant?.nameDisplay ?? "",
    automaticVlan: Boolean(r.automaticVlan),
    vlan: r.vlan ?? "",
    // legacy fields defaults
    datacenterId: r.subscriptionId ?? String(r.id ?? ""),
    direction: r.kontrolEdgePort?.tenant?.nameDisplay ?? "",
    gps: "",
    capacityKW: typeof r.mbps === "number" ? r.mbps : Number(r.mbps) || 0,
    phase: "",
    cage: "",
    rackId: "",
    rackCapacity: 0,
    patchPanelODF: "",
    rackSecurityPin: "",
    status: r.isDraft ? "Draft" : "Active",
    visible: true,
    providerId: "",
    providerCode: "",
    city: r.countryCode ?? "",
    displayName: r.name ?? r.kontrolEdgePort?.name ?? "",
  };
  });
  
/**
 * Example data for the table with all extra fields populated.
 * The table will only show Name, Direction, Capacity, Status,
 * but everything else is available for 'View' or 'Edit' in the modal.
 */

